import { BehaviorSubject, filter, Observable, switchMap, take, throwError } from 'rxjs';
import { catchError, finalize, shareReplay } from 'rxjs/operators';
import { HttpErrorResponse, HttpHandler, HttpHeaderResponse, HttpInterceptor, HttpProgressEvent, HttpRequest, HttpResponse, HttpSentEvent, HttpStatusCode, HttpUserEvent } from '@angular/common/http';
import { inject, Injectable, Injector } from '@angular/core';
import { AppSyncService } from '@app/common/services/app-sync.service';
import { AuthenticationModel } from '@app/core/services/models/authentication.model';
import { AuthManagerService } from '@app/core/services/auth-manager.service';
import { BlankMessage } from '@app/shared/models/blank-message.model';
import { DataStorage } from '@app/core/services/data-storage.service';
import { IcentralError } from '@app/shared/models/api-interfaces.model';
import { NO_DEFAULT_ACCOUNT_ERROR_CODE } from '@app/core/services/api-helper.service';
import { IdentityApiService } from '@app/core/services/identity.service';
import { LocalCacheService } from '@app/common/services/local-cache.service';
import { Router } from '@angular/router';

@Injectable()
export class CentralHttpInterceptor implements HttpInterceptor {
    private authManager = inject(AuthManagerService);
    private dataStore = inject(DataStorage);
    private injector = inject(Injector);
    private router = inject(Router);
    private localCacheService = inject(LocalCacheService);

    isRefreshingToken = false;
    tokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

    private inFlightRequests = new Map<string, Observable<HttpSentEvent | HttpHeaderResponse | HttpProgressEvent | HttpResponse<any> | HttpUserEvent<any>>>();

    intercept(req: HttpRequest<any>, next: HttpHandler):
        Observable<HttpSentEvent | HttpHeaderResponse | HttpProgressEvent | HttpResponse<any> | HttpUserEvent<any>> {
        const accessToken = this.localCacheService.iCentralApiAccessToken;
        let copiedRequest = req;

        // Send the AppSync Channel Name in the header (if it has been set).
        if (AppSyncService.channelName != null) {
            copiedRequest = copiedRequest.clone({ headers: copiedRequest.headers.append('AppSync-Channel', AppSyncService.channelName) });
        }

        if (accessToken && accessToken !== '') {
            copiedRequest = copiedRequest.clone({
                headers: copiedRequest.headers
                    .append('Authorization', `bearer ${accessToken}`)
            });
        }

        // helper that applies your existing error handling pipeline
        const handleWithErrors = (request: HttpRequest<any>) =>
            next.handle(request).pipe(
                // Terminal path always rethrows the HttpErrorResponse (`error`), not the
                // `caught` observable. Callers that relied on the old `caught` payload should
                // treat the emitted value as HttpErrorResponse going forward.
                catchError((error: Response) => {
                    if (error instanceof HttpErrorResponse) {
                        if (request.headers.get('x-skip-global-error') === 'true') {
                            return throwError(() => error);
                        }

                        switch ((<HttpErrorResponse>error).status) {
                            case HttpStatusCode.Unauthorized:
                                return this.handle401Error(request, next);
                            default:
                                this.handleError(error);
                        }

                        // Allow our customized errors to be returned.
                        if ((<any>error)?.error?.error_1 && (<any>error)?.error?.error_1[0] != null) {
                            return throwError(() => error);
                        }

                        return throwError(() => error);
                    } else {
                        return throwError(() => error);
                    }
                })
            );

        // 🔹 In-flight dedup logic (for GET requests, can be extended if needed)
        if (copiedRequest.method === 'GET' && copiedRequest.headers.get('x-bypass-dedup') !== 'true') {
            const key = this.buildInFlightKey(copiedRequest);
            const existing = this.inFlightRequests.get(key);

            if (existing) {
                // Reuse the existing in-flight observable
                return existing;
            }

            const shared$ = handleWithErrors(copiedRequest).pipe(
                shareReplay(1),
                finalize(() => {
                    this.inFlightRequests.delete(key);
                })
            );

            this.inFlightRequests.set(key, shared$);
            return shared$;
        }

        // Non-GETs fall back to your normal pipeline
        return handleWithErrors(copiedRequest);
    }

    private buildInFlightKey(req: HttpRequest<any>): string {
        // For GET, urlWithParams is usually sufficient.
        // Extend with headers/body if you need more strictness later.
        return `${req.method} ${req.urlWithParams}`;
    }

    private handle401Error(req: HttpRequest<any>, next: HttpHandler) {
        // Handle standard API missing or expired token.
        if (!this.isRefreshingToken) {
            this.isRefreshingToken = true;

            // Reset here so that the following requests wait until the token comes back from the refreshToken call.
            this.tokenSubject.next(null);

            const identityApiService = this.injector.get(IdentityApiService);

            return identityApiService.getICentralTokenFromRefreshToken()
                .pipe(
                    switchMap((authModel: AuthenticationModel) => {
                        this.tokenSubject.next(authModel.token);
                        // set, not append: the request already carries the expired Authorization header
                        // from intercept(), and appending sent both values (AB#23874).
                        const copiedRequest = req.clone({ headers: req.headers.set('Authorization', `bearer ${authModel.token}`) });

                        this.isRefreshingToken = false;
                        return next.handle(copiedRequest);
                    })
                );
        } else {
            return this.tokenSubject
                .pipe(
                    filter(token => token != null),
                    take(1),
                    switchMap(token => {
                        // The waiting request retries with the refreshed token in the same header the
                        // first one uses. It used to go into an "Authentication" query parameter the API
                        // does not read, so the retry 401'd again and started another refresh (AB#23874).
                        const copiedRequest = req.clone({ headers: req.headers.set('Authorization', `bearer ${token}`) });
                        return next.handle(copiedRequest);
                    })
                );
        }
    }

    private handleError(error: HttpErrorResponse) {
        let errorMessage = '';
        let iCentralError = error.error as IcentralError;
        let autoNav = '';

        if (!error.ok) {

            const message = {
                pageTitle: `Error: ${error.status}`,
                messageTitle: '',
                messageBody: `${errorMessage}`
            } as BlankMessage;

            switch (error.status) {
                case 400:
                    if (!error.error) { break; }

                    switch (error.error.code) {
                        case 10006:
                            message.pageSubtitle = 'Invalid password/Expired Link';
                            message.messageBody = iCentralError ? iCentralError.detail : 'Invalid password/Expired Link';
                            break;
                        case NO_DEFAULT_ACCOUNT_ERROR_CODE:
                            autoNav = '/home/admin';
                            break;
                    }
                    break;
                case 401:
                    this.authManager.isLoggedIn = false;
                    message.pageSubtitle = 'Unauthorized';
                    message.messageBody = iCentralError ? iCentralError.detail : 'Invalid Username / Password';
                    autoNav = '/login/';
                    break;
                case 403:
                    message.pageSubtitle = 'Forbidden';
                    message.messageBody = iCentralError ? iCentralError.detail : 'The resource is not available with current credentials.';
                    break;
                case 404:
                    message.pageSubtitle = 'Not Found';
                    message.messageBody = `The page you requested could not be found, either contact your webmaster or try again.
                                Use your browsers BACK button to navigate to the page you have previously come from.`;
                    break;
                case 500:
                    message.pageSubtitle = 'Internal Server Error';
                    message.messageTitle = 'Technical Error';
                    message.messageBody = 'We are working hard to correct this issue. Please wait a few moments and try again.';
                    break;
                default:
                    break;
            }

            if (iCentralError && error.status != 500) {
                iCentralError.status = error.status;
                message.messageBody = iCentralError.detail;
                message.messageTitle = `ICentral Server Error: ${iCentralError.code}`;
            } else {
                iCentralError =
                    {
                        status: error.status
                    } as IcentralError;
            }

            this.dataStore.storeError(iCentralError);
            this.dataStore.storeData(message);
            if (autoNav.length != 0) {
                this.router.navigate([autoNav]);
            }
            return throwError(() => error);

        } else {
            // server-side error
            // errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
            return;
        }
    }

}
